import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SetEntornoEmp } from "../redux/store/Actions";
import { motion } from "framer-motion";
import { FaBuilding, FaUserTie, FaHandshake } from "react-icons/fa";
import { getBusinessGroupName } from "../../theme/businessThemes";

const logo = require("../Login/digitalProf-it.png");

interface Acceso {
  id: number;
  id_client: number;
  id_profile: number;
  profile: string;
  client_name: string;
  idbusiness_group: number;
}

const SeleccionarPanel: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fallback desde localStorage si se duplica pestaña
  const state = location.state as { user: any; token: string; accesos: Acceso[] } | null;
  const stored = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
  const user   = state?.user   ?? stored?.user;
  const token  = state?.token  ?? stored?.token  ?? localStorage.getItem("authToken") ?? "";
  const accesos: Acceso[] = state?.accesos ?? stored?.accesos ?? [];

  useEffect(() => {
    if (!user || !token || accesos.length === 0) navigate("/", { replace: true });
  }, [user, token, accesos, navigate]);

  useEffect(() => {
    document.body.style.background = "#151D20";
    return () => { document.body.style.background = ""; };
  }, []);

  const seleccionar = (acceso: Acceso) => {
    const userPayload = {
      first_name: user.first_name, id_user: user.id_user,
      last_name: user.last_name,   profile: acceso.profile,
      token, user: user.user_email,
      idbusiness_group: acceso.idbusiness_group,
      business_group_name: getBusinessGroupName(acceso.idbusiness_group),
      id_client: acceso.id_client, client_name: acceso.client_name,
    };
    dispatch(SetEntornoEmp(userPayload));
    localStorage.setItem("user", JSON.stringify({ user, token, accesos }));
    localStorage.setItem("authToken", token);
    localStorage.setItem("userData", JSON.stringify(userPayload));
    navigate("/home");
  };

  const iconoPerfil = (profile: string) => {
    if (profile === "Administrador") return <FaUserTie size={22} />;
    if (profile === "Evaluador")     return <FaHandshake size={22} />;
    return <FaBuilding size={22} />;
  };

  if (!user) return null;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#ffffff", display: "flex", flexDirection: "column" }}>

      {/* HEADER */}
      <header style={{
        backgroundColor: "#151D20", padding: "16px 32px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
      }}>
        <img src={logo} alt="Logo" style={{ maxHeight: "34px", filter: "brightness(0) invert(1)" }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "11px", color: "#9BDD33", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Bienvenido
          </div>
          <div style={{ color: "#ffffff", fontWeight: 700, fontSize: "14px" }}>
            {user.first_name} {user.last_name}
          </div>
        </div>
      </header>

      {/* CENTRO */}
      <main style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "32px 16px" }}>
        <motion.div
          initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          style={{
            width: "100%", maxWidth: "1200px", backgroundColor: "#ffffff",
            borderRadius: "16px", padding: "1.25rem",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
            textAlign: "center",
          }}
        >
          <p style={{ color: "#999", fontSize: "12px", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Bienvenido, {user.first_name}
          </p>
          <h2 style={{ color: "#151D20", fontWeight: 900, fontSize: "1.9rem", marginBottom: "4px" }}>
            Selecciona tu perfil
          </h2>
          <p style={{ color: "#999", fontSize: "14px", marginBottom: "30px" }}>
            Elige el módulo al que deseas acceder
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "14px", alignItems: "center" }}>
            {accesos.filter((acceso: Acceso) => acceso.profile !== "Colaborador").map((acceso: Acceso) => (
              <motion.button
                key={acceso.id}
                whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                onClick={() => seleccionar(acceso)}
                style={{
                  width: "100%", maxWidth: "520px", border: "none",
                  borderRadius: "14px", padding: "18px 20px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "16px",
                  backgroundColor: "#151D20", boxShadow: "0 8px 20px rgba(21,29,32,0.18)",
                }}
              >
                <div style={{
                  width: "44px", height: "44px", borderRadius: "10px",
                  backgroundColor: "#9BDD33", color: "#151D20",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  {iconoPerfil(acceso.profile)}
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={{ color: "#ffffff", fontWeight: 800, fontSize: "15px" }}>
                    {acceso.profile}
                  </div>
                  {acceso.profile !== "Administrador" && (
                    <div style={{ color: "#d8d8d8", fontSize: "12px", marginTop: "2px" }}>
                      {getBusinessGroupName(acceso.idbusiness_group)}
                      {acceso.client_name ? ` — ${acceso.client_name}` : ""}
                    </div>
                  )}
                </div>
                <div style={{
                  backgroundColor: "#9BDD33", color: "#151D20",
                  width: "34px", height: "34px", borderRadius: "8px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "20px", fontWeight: "bold",
                }}>›</div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </main>

      {/* FOOTER */}
      <footer style={{
        backgroundColor: "#FFFFFF", color: "#151D20",
        textAlign: "center", padding: "16px", fontSize: "12px", flexShrink: 0, width: "100%",
      }}>
        © DIGITAL PROFIT SAS COLOMBIA {new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default SeleccionarPanel;